import { create } from "zustand"

import type { SalesOrderSearchParams } from "../schemas"

import { GetSalesOrderListDocument, type GetSalesOrderListQuery, type SalesOrder } from "@/graphql/codegen/graphql"
import { createSearchStore } from "@/stores/createSearchStore"

export type SalesOrderSearchFilters = SalesOrderSearchParams & {
  companyId: string
  shipToLocationId: string
}

const deduplicateSalesOrders = (newData: SalesOrder[], existingData: SalesOrder[]): SalesOrder[] => {
  const uniqueIds = new Set(existingData.map((salesOrder) => salesOrder.salesOrderId))
  return [...existingData, ...newData.filter((salesOrder) => !uniqueIds.has(salesOrder.salesOrderId))]
}

export const useSalesOrderSearchStore = create(
  createSearchStore<SalesOrder, SalesOrderSearchFilters, GetSalesOrderListQuery>({
    documentNode: GetSalesOrderListDocument,
    deduplicateData: deduplicateSalesOrders,

    getQueryInput: (filters: SalesOrderSearchFilters, offset: number, limit: number) => {
      const { companyId, shipToLocationId, query, statuses, dateFrom, dateTo, dateRangeType } = filters
      return {
        filter: {
          companyId,
          shipToLocationId,
          query,
          statuses,
          dateFrom,
          dateTo,
          dateRangeType,
        },
        offset,
        limit,
      }
    },

    getQueryVariables: (queryInput) => ({ input: queryInput }),

    extractDataFromResponse: (data) => ({
      __typename: data.salesOrder.list.__typename,
      data:
        data.salesOrder.list.__typename === "SalesOrderListSuccess"
          ? (data.salesOrder.list.salesOrders as SalesOrder[])
          : [],
      error: data.salesOrder.list.__typename === "SalesOrderListFailure" ? data.salesOrder.list.error : undefined,
    }),

    hasMoreData: (response, state) => response.data.length === state.limit,
  })
)
